import React from "react";
import {useTranslation} from "react-i18next";
import Grid from "@material-ui/core/Grid";
import {Organization} from "../models/organization";
import {mergeCustomers} from "../api/organization-functions";
import {useApiNotification} from "../components/ui/ApiNotificationProvider";
import {Typography} from "@material-ui/core";
import {TabData, Wizard} from "../components/_shared/wizard/Wizard";
import {WizardNavigation, WizardNavigationButton} from "../components/_shared/wizard/WizardNavigation";
import {OrganizationsTableConnected} from "../components/organizations/organization/OrganizationsTableConnected";
import Checkbox from "@material-ui/core/Checkbox";
import {ValidationRow} from "../components/_shared/wizard/ValidationRow";
import {FormStatus} from "../models/shared";
import {makeStyles} from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import {grey} from "@material-ui/core/colors";
import ReactsCard from "../components/ui/ReactsCard";

const useStyles = makeStyles(theme => ({
    root: {
        minWidth: 250,
        margin: theme.spacing(4)
    },
    sectionTitle: {
        fontWeight: 'bold'
    },
    step: {
        marginTop: theme.spacing(4)
    },
    tableHeader: {
        borderBottom: `1px solid ${grey[300]}`
    },
    verticallyCenteredRow: {
        paddingTop: 0,
        marginBottom: theme.spacing(4)
    }
}))

const MergeCustomers: React.FC = () => {
    const {t} = useTranslation();
    const classes = useStyles();
    const [processingStatus, setProcessingStatus] = React.useState(FormStatus.none);
    const [targetOrganizationId, setTargetOrganizationId] = React.useState<number | null>(null);
    const [targetOrganizationName, setTargetOrganizationName] = React.useState<string | null>(null);
    const [sourceOrganizationId, setSourceOrganizationId] = React.useState<number | null>(null);
    const [sourceOrganizationName, setSourceOrganizationName] = React.useState<string | null>(null);
    const {handleError, showSuccess} = useApiNotification();
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const selectSourceOrganization = (o: Organization) => <Checkbox value={o.id} checked={o.id === sourceOrganizationId} color="primary" onChange={(e) => {
        if (e.target.checked) {
            setSourceOrganizationId(o.id);
            setSourceOrganizationName(o.name);
        } else {
            setSourceOrganizationId(null);
            setSourceOrganizationName('');
        }
    }} disabled={!!sourceOrganizationId && o.id !== sourceOrganizationId || o.id === targetOrganizationId}></Checkbox>;

    const selectTargetOrganization = (o: any) => <Checkbox value={o.id} checked={o.id === targetOrganizationId} color="primary" onChange={(e) => {
        if (e.target.checked) {
            setTargetOrganizationId(o.id);
            setTargetOrganizationName(o.name);
        } else {
            setTargetOrganizationId(null);
            setTargetOrganizationName('');
        }
    }} disabled={!!targetOrganizationId && o.id !== targetOrganizationId || o.id === sourceOrganizationId}></Checkbox>;

    const onSave = async () => {
        if (sourceOrganizationId && targetOrganizationId){
            setProcessingStatus(FormStatus.processing);
            const result = await mergeCustomers(sourceOrganizationId, targetOrganizationId).catch((err) => {
                handleError(err);
                setErrorMessage('_Common.Api.Messages.UnknownError');
                setProcessingStatus(FormStatus.failed);
            });
            if (result) {
                setProcessingStatus(FormStatus.successful);
                showSuccess();
                setErrorMessage('');
            } else {
                setProcessingStatus(FormStatus.failed);
                handleError({customMessage: t('MergeCustomers.ValidationError')});
                setErrorMessage('MergeCustomers.ValidationError')
            }
        }
    }

    const onClose = () => {
        globalThis.location.reload();
    };

    const dynamicOnClose = (): (() => void) | null => {
        if (processingStatus === FormStatus.successful || processingStatus === FormStatus.failed) {
            return onClose;
        } else {
            return null;
        }
    };

    const getDisabledButtons = () => {
        if (processingStatus === FormStatus.processing) {
            return new Set([WizardNavigationButton.Previous, WizardNavigationButton.Close, WizardNavigationButton.Next, WizardNavigationButton.Save]);
        } else {
            return new Set<WizardNavigationButton>();
        }
    }

    const getStep1DisabledButtons = () => {
        if (sourceOrganizationId) {
            return new Set<WizardNavigationButton>();
        } else {
            return new Set([WizardNavigationButton.Next]);
        }
    };

    const getStep2DisabledButtons = () => {
        if (targetOrganizationId) {
            return new Set<WizardNavigationButton>();
        } else {
            return new Set([WizardNavigationButton.Next]);
        }
    };

    const step1: TabData = {
        labelKey: 'MergeCustomers.SourceCustomer',
        key: 'merge-customer-select-source',
        preserveTab: true,
        isDisabled: true,
        childComponent: (index: number, lastIndex: number, onChangeTab: (newTab: number) => void) => <Box className={classes.step}>
            <OrganizationsTableConnected actions={selectSourceOrganization}></OrganizationsTableConnected>
            <WizardNavigation onChangeTab={onChangeTab} index={index} lastIndex={lastIndex} onClose={null} disabledButtons={getStep1DisabledButtons()}></WizardNavigation>
        </Box>
    };
    const step2: TabData = {
        labelKey: 'MergeCustomers.TargetCustomer',
        key: 'merge-customer-select-destination',
        preserveTab: true,
        isDisabled: true,
        childComponent: (index: number, lastIndex: number, onChangeTab: (newTab: number) => void) => <Box className={classes.step}>
            <OrganizationsTableConnected actions={selectTargetOrganization}></OrganizationsTableConnected>
            <WizardNavigation onChangeTab={onChangeTab} index={index} lastIndex={lastIndex} onClose={null} disabledButtons={getStep2DisabledButtons()}></WizardNavigation>
        </Box>
    };
    const step3: TabData = {
        labelKey: 'Machine.MoveMachines.Tabs.Validation.Title',
        key: 'merge-customer-validation',
        preserveTab: true,
        isDisabled: true,
        childComponent: (index: number, lastIndex: number, onChangeTab: (newTab: number) => void) => <Grid container spacing={3} className={classes.step}>
            <Grid container spacing={4}>
                <Grid container item xs={12}>
                    <Grid container item xs={12} className={classes.tableHeader}>
                        <Grid container item xs={11} spacing={4}>
                            <Grid item xs={4}>
                                <Typography variant="body1" className={classes.sectionTitle}>{t('MergeCustomers.Source')}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body1" className={classes.sectionTitle}>{t('MergeCustomers.Target')}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <ValidationRow processingStatus={processingStatus} errorMessage={errorMessage}>
                    <Grid container item xs={11} spacing={4}>
                        <Grid item xs={4} className={classes.verticallyCenteredRow}>
                            <Typography variant="body1">{sourceOrganizationName}</Typography>
                        </Grid>
                        <Grid item xs={4} className={classes.verticallyCenteredRow}>
                            <Typography variant="body1">{targetOrganizationName}</Typography>
                        </Grid>
                    </Grid>
                </ValidationRow>
            </Grid>
            <WizardNavigation onChangeTab={onChangeTab} index={index} lastIndex={lastIndex} onClose={dynamicOnClose()}
                              allCompleted={processingStatus == FormStatus.successful || processingStatus == FormStatus.failed}
                              disabledButtons={getDisabledButtons()}
                              onSave={onSave}
            ></WizardNavigation>
        </Grid>
    };

    return <ReactsCard title={t('MergeCustomers.Title')} className={classes.root}>
        <Wizard tabs={[step1, step2, step3]}></Wizard>
    </ReactsCard>;
}

export default MergeCustomers;
